import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import ActionButtons from "../../components/common/ActionButtons";
import {
  useCreateStationMutation,
  useGetLocalDevicesQuery,
  useGetStationsQuery,
  useUpdateStationMutation,
} from "../../store/api";
import { getStationErrorMessage } from "../../utils/stationErrorHandler";
import { useStoreFeatures } from "../../hooks/useStoreFeatures";
import { hasAnyScreenAccess } from "../../utils/permissions";

const initialForm = {
  stationCode: "",
  displayName: "",
  sort: 0,
  isActive: true,
  printPrefix: "",
  isDisplayActive: true,
  isStandaloneDisplay: false,
  standaloneKdsDeviceId: "",
};

const getStandaloneKdsDeviceId = (station) =>
  (Array.isArray(station?.assignedKdsDeviceIds)
    ? station.assignedKdsDeviceIds[0]
    : null) ||
  (Array.isArray(station?.AssignedKdsDeviceIds)
    ? station.AssignedKdsDeviceIds[0]
    : null) ||
  station?.kdsDeviceId ||
  station?.KdsDeviceId ||
  "";

const getNestedDisplayActive = (display) => {
  if (!display || typeof display !== "object") return undefined;
  if (display.active !== undefined) return Boolean(display.active);
  if (display.isActive !== undefined) return Boolean(display.isActive);
  return undefined;
};

const buildDisplayPayload = (source, isDisplayActive) => ({
  isKitchenDisplayActive: isDisplayActive,
  isOrderDisplayActive: isDisplayActive,
  KitchenDisplay: {
    ...(source?.KitchenDisplay && typeof source.KitchenDisplay === "object"
      ? source.KitchenDisplay
      : {}),
    active: isDisplayActive,
  },
  OrderDisplay: {
    ...(source?.OrderDisplay && typeof source.OrderDisplay === "object"
      ? source.OrderDisplay
      : {}),
    active: isDisplayActive,
  },
});

const Stations = () => {
  const user = useSelector((state) => state.auth.user);
  const {
    features,
    isLoading: featuresLoading,
    isError: featuresError,
    refetch: refetchFeatures,
  } = useStoreFeatures(true);
  const { data, isLoading, isError, error, refetch } = useGetStationsQuery();
  const [createStation, createState] = useCreateStationMutation();
  const [updateStation, updateState] = useUpdateStationMutation();
  const [form, setForm] = useState(initialForm);
  const [editingStationId, setEditingStationId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [localError, setLocalError] = useState("");
  const {
    data: localDevicesData,
    isLoading: kdsDevicesLoading,
    isError: kdsDevicesError,
    refetch: refetchKdsDevices,
  } = useGetLocalDevicesQuery("KDS", {
    skip: !showForm || !form.isStandaloneDisplay,
  });

  const stations = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const kdsDevices = useMemo(
    () =>
      (Array.isArray(localDevicesData)
        ? localDevicesData
        : Array.isArray(localDevicesData?.devices)
          ? localDevicesData.devices
          : [])
        .filter(
          (device) =>
            String(device?.deviceType || "").toUpperCase() === "KDS" &&
            device?.isActive !== false,
        )
        .sort((a, b) =>
          String(a?.deviceName || "").localeCompare(
            String(b?.deviceName || ""),
          ),
        ),
    [localDevicesData],
  );
  const kdsAssignments = useMemo(() => {
    const assignments = new Map();
    stations.forEach((station) => {
      const deviceId = getStandaloneKdsDeviceId(station);
      if (deviceId) assignments.set(deviceId, station);
    });
    return assignments;
  }, [stations]);

  const canKds =
    Boolean(features?.enableKds) &&
    hasAnyScreenAccess(user, ["SETTINGS_FULFILLMENT"]);
  const canOrderDisplay =
    Boolean(features?.enableOrderDisplay) &&
    hasAnyScreenAccess(user, ["DEVICES_HEALTH", "DEVICES_LOCAL_DEVICES", "DEVICES_TERMINALS", "SETTINGS_FULFILLMENT"]);

  const createOrUpdateError = createState.error || updateState.error;

  const sortedStations = useMemo(
    () => [...stations].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)),
    [stations],
  );

  const getDisplayActiveValue = (station) => {
    const kitchenDisplayActive =
      station.isKitchenDisplayActive !== undefined
        ? station.isKitchenDisplayActive
        : getNestedDisplayActive(station.KitchenDisplay);
    const orderDisplayActive =
      station.isOrderDisplayActive !== undefined
        ? station.isOrderDisplayActive
        : getNestedDisplayActive(station.OrderDisplay);

    return kitchenDisplayActive !== false && orderDisplayActive !== false;
  };

  if (featuresLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Station Master" subtitle="Loading store features..." />
        <Card>
          <Alert tone="info">Loading store features...</Alert>
        </Card>
      </div>
    );
  }

  if (featuresError || !features) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Station Master"
          subtitle="Unable to load store features."
        />
        <Card className="space-y-3">
          <Alert tone="error">Failed to load store features.</Alert>
          <Button size="sm" onClick={() => refetchFeatures && refetchFeatures()}>
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (name === "stationCode") {
      setForm((prev) => ({ ...prev, stationCode: value.toUpperCase() }));
      return;
    }
    if (name === "isStandaloneDisplay" && type === "checkbox") {
      setLocalError("");
      setForm((prev) => ({
        ...prev,
        isStandaloneDisplay: checked,
        isActive: checked ? true : prev.isActive,
        standaloneKdsDeviceId: checked ? prev.standaloneKdsDeviceId : "",
      }));
      return;
    }
    if (name === "standaloneKdsDeviceId") {
      setLocalError("");
    }
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLocalError("");
    if (form.isStandaloneDisplay && !form.standaloneKdsDeviceId) {
      setLocalError("Select the KDS device that should display this station.");
      return;
    }
    if (form.isStandaloneDisplay && kdsDevicesError) {
      setLocalError("KDS devices could not be loaded. Retry before saving.");
      return;
    }
    if (form.isStandaloneDisplay && kdsDevicesLoading) {
      setLocalError("Wait for the KDS devices to finish loading.");
      return;
    }
    if (form.isStandaloneDisplay) {
      const selectedKds = kdsDevices.find(
        (device) => device.deviceId === form.standaloneKdsDeviceId,
      );
      if (!selectedKds) {
        setLocalError("Select an active KDS device before saving.");
        return;
      }
      const assignedStation = kdsAssignments.get(form.standaloneKdsDeviceId);
      if (
        assignedStation &&
        assignedStation.stationId !== editingStationId
      ) {
        setLocalError(
          `This KDS is already assigned to ${assignedStation.displayName || assignedStation.stationCode}.`,
        );
        return;
      }
    }
    const nextIsActive = form.isStandaloneDisplay ? true : form.isActive;
    const payload = {
      stationCode: form.stationCode.trim().toUpperCase(),
      displayName: form.displayName.trim(),
      sort: Number(form.sort) || 0,
      isActive: nextIsActive,
      printPrefix: form.printPrefix.trim().toUpperCase(),
      isStandaloneDisplay: form.isStandaloneDisplay,
      assignedKdsDeviceIds: form.isStandaloneDisplay
        ? [form.standaloneKdsDeviceId]
        : [],
      ...buildDisplayPayload(null, form.isDisplayActive),
    };

    try {
      if (editingStationId) {
        await updateStation({
          stationId: editingStationId,
          payload,
        }).unwrap();
      } else {
        await createStation(payload).unwrap();
      }

      setForm(initialForm);
      setEditingStationId(null);
      setShowForm(false);
      createState.reset();
      updateState.reset();
      refetch();
    } catch (err) {
      setLocalError(getStationErrorMessage(err, "Failed to save station."));
    }
  };

  const handleEdit = (station) => {
    createState.reset();
    updateState.reset();
    setLocalError("");
    setForm({
      stationCode: station.stationCode || "",
      displayName: station.displayName || "",
      sort: station.sort ?? 0,
      isActive: station.isActive !== undefined ? station.isActive : true,
      printPrefix: station.printPrefix || "",
      isDisplayActive: getDisplayActiveValue(station),
      isStandaloneDisplay:
        station.isStandaloneDisplay !== undefined
          ? station.isStandaloneDisplay
          : station.IsStandaloneDisplay !== undefined
            ? station.IsStandaloneDisplay
            : false,
      standaloneKdsDeviceId: getStandaloneKdsDeviceId(station),
    });
    setEditingStationId(station.stationId);
    setShowForm(true);
  };

  const handleToggleDisplayState = async (station, fieldName) => {
    setLocalError("");
    const currentDisplayActive = getDisplayActiveValue(station);
    const nextDisplayActive =
      fieldName === "isDisplayActive"
        ? !currentDisplayActive
        : currentDisplayActive;
    const nextStandaloneDisplay =
      fieldName === "isStandaloneDisplay"
        ? !Boolean(
            station.isStandaloneDisplay !== undefined
              ? station.isStandaloneDisplay
              : station.IsStandaloneDisplay,
          )
        : station.isStandaloneDisplay !== undefined
          ? station.isStandaloneDisplay
          : station.IsStandaloneDisplay !== undefined
            ? station.IsStandaloneDisplay
            : false;
    const nextIsActive =
      fieldName === "isStandaloneDisplay" && nextStandaloneDisplay
        ? true
        : station.isActive !== undefined
          ? station.isActive
          : true;

    if (
      fieldName === "isStandaloneDisplay" &&
      nextStandaloneDisplay &&
      !getStandaloneKdsDeviceId(station)
    ) {
      setLocalError(
        "Edit this station and select a KDS device before enabling standalone display.",
      );
      return;
    }

    try {
      await updateStation({
        stationId: station.stationId,
        payload: {
          stationCode: station.stationCode?.trim().toUpperCase() || "",
          displayName: station.displayName?.trim() || "",
          sort: Number(station.sort) || 0,
          printPrefix: station.printPrefix?.trim().toUpperCase() || "",
          isActive: nextIsActive,
          isStandaloneDisplay: nextStandaloneDisplay,
          assignedKdsDeviceIds: nextStandaloneDisplay
            ? [getStandaloneKdsDeviceId(station)].filter(Boolean)
            : [],
          ...buildDisplayPayload(station, nextDisplayActive),
        },
      }).unwrap();
      refetch();
    } catch (err) {
      setLocalError(
        getStationErrorMessage(err, "Failed to update station display state."),
      );
    }
  };

  const handleToggleActiveState = async (station) => {
    setLocalError("");
    const nextIsActive =
      station.isActive !== undefined ? !Boolean(station.isActive) : false;
    const currentStandaloneDisplay =
      station.isStandaloneDisplay !== undefined
        ? station.isStandaloneDisplay
        : station.IsStandaloneDisplay !== undefined
          ? station.IsStandaloneDisplay
          : false;
    const currentDisplayActive = getDisplayActiveValue(station);

    try {
      await updateStation({
        stationId: station.stationId,
        payload: {
          stationCode: station.stationCode?.trim().toUpperCase() || "",
          displayName: station.displayName?.trim() || "",
          sort: Number(station.sort) || 0,
          printPrefix: station.printPrefix?.trim().toUpperCase() || "",
          isActive: nextIsActive,
          isStandaloneDisplay: currentStandaloneDisplay,
          assignedKdsDeviceIds: currentStandaloneDisplay
            ? [getStandaloneKdsDeviceId(station)].filter(Boolean)
            : [],
          ...buildDisplayPayload(station, currentDisplayActive),
        },
      }).unwrap();
      refetch();
    } catch (err) {
      setLocalError(
        getStationErrorMessage(err, "Failed to update station active state."),
      );
    }
  };

  const columns = [
    { key: "code", label: "Code" },
    { key: "name", label: "Name" },
    { key: "active", label: "Active" },
    { key: "actions", label: "Actions" },
  ];

  const rows = sortedStations.map((station) => {
    const isStationActive =
      station.isActive !== undefined ? Boolean(station.isActive) : true;
    return {
      id: station.stationId,
      code: (
        <span className="font-semibold text-slate-900 dark:text-slate-100">
          {station.stationCode}
        </span>
      ),
      name: (
        <span className="text-slate-700 dark:text-slate-300">
          {station.displayName}
        </span>
      ),
      active: (
        <label className="inline-flex w-fit items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(isStationActive)}
            onChange={() => handleToggleActiveState(station)}
            disabled={updateState.isLoading}
            className="h-4 w-4 cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600 dark:bg-slate-800"
            aria-label={
              isStationActive
                ? `Mark ${station.displayName || station.stationCode} inactive`
                : `Mark ${station.displayName || station.stationCode} active`
            }
          />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {isStationActive ? "Active" : "Inactive"}
          </span>
        </label>
      ),
      actions: (
        <div className="flex items-center">
          <ActionButtons
            actions={[
              {
                icon: "edit",
                label: "Edit",
                color: "primary",
                onClick: () => handleEdit(station),
              },
            ]}
          />
        </div>
      ),
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Station Master"
        subtitle={
          canKds || canOrderDisplay
            ? "Manage station codes, activity, and screen visibility."
            : "Manage station codes and activity."
        }
        actions={
          showForm ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                createState.reset();
                updateState.reset();
                setShowForm(false);
                setEditingStationId(null);
                setForm(initialForm);
                setLocalError("");
              }}
            >
              Back
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => {
                createState.reset();
                updateState.reset();
                setLocalError("");
                setEditingStationId(null);
                setForm(initialForm);
                setShowForm(true);
              }}
            >
              Add Station
            </Button>
          )
        }
      />

      {showForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {editingStationId ? "Edit Station" : "Add Station"}
          </h2>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-3">
              <Input
                label="Station Code"
                name="stationCode"
                placeholder="DRINKS"
                value={form.stationCode}
                onChange={handleChange}
                required
              />
              <Input
                label="Display Name"
                name="displayName"
                placeholder="Drinks"
                value={form.displayName}
                onChange={handleChange}
                required
              />
              <Input
                label="Sort"
                name="sort"
                type="number"
                value={form.sort}
                onChange={handleChange}
              />
              <Input
                label="Token Prefix"
                name="printPrefix"
                placeholder="K"
                value={form.printPrefix}
                onChange={handleChange}
                required
                // helper="This value will be printed before the token number on receipts (e.g., K-101)."
              />
              {(canKds || canOrderDisplay) && (
                <div className="flex items-center pt-6">
                  <Checkbox
                    label="Display Active"
                    name="isDisplayActive"
                    checked={form.isDisplayActive}
                    onChange={handleChange}
                    helper="Controls Kitchen Display and Order Display visibility."
                  />
                </div>
              )}
            </div>

            <Checkbox
              label="Standalone KDS Display"
              name="isStandaloneDisplay"
              checked={form.isStandaloneDisplay}
              onChange={handleChange}
            />
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Select the KDS device that should show orders for this station.
            </p>

            {form.isStandaloneDisplay && (
              <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-slate-900/30">
                <div className="mb-3">
                  <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    Display on KDS <span className="text-rose-600">*</span>
                  </h3>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Choose one active KDS for this station.
                  </p>
                </div>

                {kdsDevicesLoading && (
                  <Alert tone="info">Loading KDS devices...</Alert>
                )}

                {kdsDevicesError && (
                  <Alert tone="error">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <span>Unable to load KDS devices.</span>
                      <Button
                        size="sm"
                        variant="secondary"
                        type="button"
                        onClick={() => refetchKdsDevices()}
                      >
                        Retry
                      </Button>
                    </div>
                  </Alert>
                )}

                {!kdsDevicesLoading &&
                  !kdsDevicesError &&
                  kdsDevices.length === 0 && (
                    <Alert tone="warning">
                      No active KDS devices are available. Add or activate one
                      in Local Devices.
                    </Alert>
                  )}

                {!kdsDevicesLoading &&
                  !kdsDevicesError &&
                  kdsDevices.length > 0 && (
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                      {kdsDevices.map((device) => {
                        const assignedStation = kdsAssignments.get(
                          device.deviceId,
                        );
                        const assignedElsewhere = Boolean(
                          assignedStation &&
                            assignedStation.stationId !== editingStationId,
                        );
                        const selected =
                          form.standaloneKdsDeviceId === device.deviceId;

                        return (
                          <label
                            key={device.deviceId}
                            className={[
                              "flex gap-3 rounded-lg border p-3 transition",
                              assignedElsewhere
                                ? "cursor-not-allowed border-slate-200 bg-slate-100 opacity-65 dark:border-slate-700 dark:bg-slate-800"
                                : "cursor-pointer bg-white hover:border-indigo-300 dark:bg-slate-900 dark:hover:border-indigo-600",
                              selected
                                ? "border-indigo-500 ring-2 ring-indigo-500/15"
                                : "border-slate-200 dark:border-slate-700",
                            ].join(" ")}
                          >
                            <input
                              type="radio"
                              name="standaloneKdsDeviceId"
                              value={device.deviceId}
                              checked={selected}
                              onChange={handleChange}
                              disabled={assignedElsewhere}
                              className="mt-0.5 h-4 w-4 shrink-0 border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800"
                            />
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                                {device.deviceName || "Unnamed KDS"}
                              </span>
                              <span className="mt-0.5 block truncate text-xs text-slate-500 dark:text-slate-400">
                                {device.deviceKey || "No device key"}
                              </span>
                              {assignedElsewhere && (
                                <span className="mt-1 block text-xs font-medium text-amber-700 dark:text-amber-300">
                                  Assigned to{" "}
                                  {assignedStation.displayName ||
                                    assignedStation.stationCode}
                                </span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
              </div>
            )}

            {localError && <Alert tone="error">{localError}</Alert>}
            {(createState.isError || updateState.isError) && (
              <Alert tone="error">
                {getStationErrorMessage(
                  createOrUpdateError,
                  "Failed to save station.",
                )}
              </Alert>
            )}
            {(createState.isSuccess || updateState.isSuccess) && (
              <Alert tone="success">
                Station {editingStationId ? "updated" : "created"} successfully.
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={
                  createState.isLoading ||
                  updateState.isLoading ||
                  (form.isStandaloneDisplay && kdsDevicesLoading)
                }
              >
                {createState.isLoading || updateState.isLoading
                  ? "Saving..."
                  : editingStationId
                    ? "Update Station"
                    : "Create Station"}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!showForm && (
        <Card className="p-0">
          <div className="border-b border-slate-100 px-6 py-4 text-sm font-semibold text-slate-700 dark:border-slate-700 dark:text-slate-300">
            Stations
          </div>
          <div className="px-6 py-4">
            {localError && <Alert tone="error">{localError}</Alert>}
            {isLoading && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Loading stations...
              </p>
            )}
            {isError && (
              <Alert tone="error">
                {getStationErrorMessage(error, "Failed to load stations.")}
              </Alert>
            )}
            {!isLoading && !isError && (
              <Table columns={columns} rows={rows} emptyText="No stations found." />
            )}
          </div>
        </Card>
      )}

    </div>
  );
};

export default Stations;

